import { memo } from 'react'
import Typography from '@material-ui/core/Typography'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import { alpha } from '@material-ui/core/styles/colorManipulator'
import CheckIcon from '@material-ui/icons/Check'
import Skeleton from '@material-ui/lab/Skeleton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'

import { scrollToTop } from '../../../utils/pollard/scroll'
import { Logo, bankLogos } from '../../BankMagnifier/LogoBoard'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    marginTop: theme.spacing(4)
  },
  fakeOffer: {
    marginBottom: theme.spacing(4),
    borderRadius: '5px',
    boxShadow: '1px 2px 4px rgb(0 2 6 / 20%)',

    '&:nth-last-child(2)': {
      marginBottom: 0,

      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(4)
      }
    },

    '&:last-child': {
      display: 'none',

      [theme.breakpoints.up('md')]: {
        display: 'block',
        margin: 0
      }
    }
  },
  fakeOfferTitle: {
    width: '100%',
    padding: `${theme.spacing(2)}px ${theme.spacing(2.5)}px`,
    background: theme.palette.grey[50],

    '& img': {
      height: '30px'
    },

    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      background: 'none'
    }
  },
  skeleton: {
    borderRadius: '20px/40px'
  },
  skeletonsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 100%)',
    width: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '5px',

    [theme.breakpoints.up('xs')]: {
      gridTemplateColumns: 'repeat(auto-fill, 50%)'
    },

    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(auto-fill, calc(100%/3))'
    },

    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(auto-fill, 20%)'
    }
  },
  skeletonBlock: {
    padding: theme.spacing(2.5),
    zIndex: -1,

    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(7.5)
    },

    '&:last-child': {
      display: 'none',

      [theme.breakpoints.up('sm')]: {
        display: 'block'
      },

      [theme.breakpoints.up('md')]: {
        background: theme.palette.grey[50]
      }
    }
  },
  overlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    maxHeight: '100%',
    overflow: 'auto',
    padding: `${theme.spacing(7.5)}px ${theme.spacing(2.5)}px`,
    background: alpha(theme.palette.common.black, 0.8),
    color: 'white',

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(7.5)
    }
  },
  overlayTitle: {
    marginBottom: theme.spacing(6.5),

    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3.5)
    }
  },
  overlayList: {
    margin: `0 0 ${theme.spacing(6.5)}px`,
    padding: 0,
    listStyle: 'none',

    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3.5)
    }
  },
  overlayListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(6.5),

    '&:last-child': {
      margin: 0
    },

    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3.5)
    }
  },
  checkIcon: {
    fontSize: '1.125rem',
    marginRight: theme.spacing(1.5),
    color: theme.palette.success.main
  }
}))

const FAKE_BANKS = [
  'targobank',
  'ingDiba',
  'commerzbank',
  'dkb',
  'postbank',
  'deutscheBank'
]

const getSkeletonRandomWidth = (): string => {
  const MIN_WIDTH = 60
  const MAX_WIDTH = 100
  const randomWidthValue = Math.random() * (MAX_WIDTH - MIN_WIDTH) + MIN_WIDTH

  return `${randomWidthValue}%`
}

const Advantages = () => {
  const {
    root,
    fakeOffer,
    fakeOfferTitle,
    skeleton,
    skeletonsContainer,
    skeletonBlock,
    overlay,
    overlayTitle,
    overlayListItem,
    overlayList,
    checkIcon
  } = useStyles()
  const { rootStore } = useRootStore()

  return (
    <div className={root}>
      <div>
        {FAKE_BANKS.map((fakeBank) => (
          <div className={fakeOffer} key={bankLogos[fakeBank]}>
            <div className={fakeOfferTitle}>
              <Logo logoId={String(bankLogos[fakeBank])} bankName={fakeBank} />
            </div>
            <div className={skeletonsContainer}>
              {[...Array(5)].map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={skeletonBlock} key={`skeletonblock-${index}`}>
                  <Skeleton
                    className={skeleton}
                    width={getSkeletonRandomWidth()}
                    height={34}
                    animation={false}
                  />
                  <Skeleton
                    className={skeleton}
                    width={getSkeletonRandomWidth()}
                    height={20}
                    animation={false}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={overlay}>
        <Typography variant="h3" color="inherit" className={overlayTitle}>
          Warum wir Ihnen Angebote erst nach Eingabe persönlicher Daten anzeigen
        </Typography>
        <ul className={overlayList}>
          <li className={overlayListItem}>
            <CheckIcon className={checkIcon} />
            Bei smava gibt es keine Schaufenster-Zinsen.
          </li>
          <li className={overlayListItem}>
            <CheckIcon className={checkIcon} />
            Vertrauenswürdige Angebote gibt es nur nach einer
            Kreditwürdigkeitsprüfung. Dafür benötigen wir Informationen zu Ihrer
            Person und finanziellen Situation.
          </li>
          <li className={overlayListItem}>
            <CheckIcon className={checkIcon} />
            Nach Eingabe dieser Angaben erhalten Sie von uns einen Überblick
            über die für Sie verfügbaren Kreditangebote.
          </li>
        </ul>
        <Button
          id="scroll-to-top"
          variant="contained"
          fullWidth
          color="primary"
          onClick={() => scrollToTop(rootStore.isInIframe)}
        >
          Kreditvergleich starten
        </Button>
      </div>
    </div>
  )
}

export default memo(Advantages)
